import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../providers/AuthProvider/AuthProvider';
import {
  AUTH_CALLBACK_ERROR_QUERY_PARAM,
  consumePostLoginRedirect,
  getAuthCallbackLoginErrorPath,
  isAuthCallbackError,
  sanitizePostLoginRedirect,
} from '../utils/authCallback';

export interface UseAuthCallbackRedirectOptions {
  defaultRedirectPath: string;
  loginPath: string;
  loginCallbackPath?: string;
  errorQueryParam?: string;
}

export type AuthCallbackRedirectStatus = 'pending' | 'redirecting';

const callbackParsePromiseCache = new Map<string, Promise<string | null | undefined>>();

const getCallbackParseCacheKey = () => {
  if (typeof window === 'undefined') {
    return 'server';
  }

  return `${window.location.pathname}${window.location.search}`;
};

export const useAuthCallbackRedirect = (options: UseAuthCallbackRedirectOptions): AuthCallbackRedirectStatus => {
  const { isAuthenticated, parseAuthToken } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<AuthCallbackRedirectStatus>('pending');

  const {
    defaultRedirectPath,
    loginPath,
    loginCallbackPath,
    errorQueryParam = AUTH_CALLBACK_ERROR_QUERY_PARAM,
  } = options;

  useEffect(() => {
    let isActive = true;

    const resolveNextPath = (stateRedirect?: string | null) => {
      const storedRedirect = sanitizePostLoginRedirect(consumePostLoginRedirect(), { loginPath, loginCallbackPath });
      const safeStateRedirect = sanitizePostLoginRedirect(stateRedirect, { loginPath, loginCallbackPath });

      const safeDefaultRedirect =
        sanitizePostLoginRedirect(defaultRedirectPath, { loginPath, loginCallbackPath }) ?? defaultRedirectPath;

      return storedRedirect ?? safeStateRedirect ?? safeDefaultRedirect;
    };

    const navigateToPath = (path: string) => {
      setStatus('redirecting');
      navigate(path, { replace: true });
    };

    const getParsePromise = () => {
      const cacheKey = getCallbackParseCacheKey();
      const cachedPromise = callbackParsePromiseCache.get(cacheKey);

      if (cachedPromise) {
        return cachedPromise;
      }

      const parsePromise = parseAuthToken().finally(() => {
        callbackParsePromiseCache.delete(cacheKey);
      });

      callbackParsePromiseCache.set(cacheKey, parsePromise);

      return parsePromise;
    };

    const redirectAfterCallback = async () => {
      try {
        const stateRedirect = await getParsePromise();

        if (!isActive) {
          return;
        }

        navigateToPath(resolveNextPath(stateRedirect));
      } catch (error) {
        if (!isActive) {
          return;
        }

        if (isAuthenticated()) {
          navigateToPath(resolveNextPath());

          return;
        }

        const errorCode = isAuthCallbackError(error) ? error.code : 'callback_failed';

        const loginErrorPath = getAuthCallbackLoginErrorPath({
          loginPath,
          errorQueryParam,
          errorCode: errorCode === 'callback_failed' ? errorCode : 'callback_failed',
        });

        console.error('Auth callback redirect failed:', error);
        navigateToPath(loginErrorPath);
      }
    };

    void redirectAfterCallback();

    return () => {
      isActive = false;
    };
  }, [defaultRedirectPath, errorQueryParam, isAuthenticated, loginCallbackPath, loginPath, navigate, parseAuthToken]);

  return status;
};
